import { useCallback, useEffect, useState } from "react";
import DataTable, { Column, FilterOption } from "../../components/commons/DataTable";
import EventAction from "../../components/partials/event/EventAction";
import EventActionExtra from "../../components/partials/event/EventActionExtra";
import type { Event, SearchEventParams } from "../../services/webServices/event/types";
import { getEvents } from "../../services/webServices/event/get";
import { useAdminLoading } from "../../context/AdminLoadingContext";
import { useInitialPageLoad } from "../../components/layouts/admin-layout/useInitialPageLoad";

const PAGE_SIZE = 12;

const filterOptions: FilterOption[] = [
  { value: "draft", label: "پیش‌نویس" },
  { value: "awaiting_confirmation", label: "در انتظار تایید" },
  { value: "published", label: "منتشر شده" }
];

export default function Event() {

  const [data, setData] = useState<Event[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [searchValue, setSearchValue] = useState<string>("");
  const [filterValue, setFilterValue] = useState<string>("");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalItems, setTotalItems] = useState<number>(0);

  const { showLoading, hideLoading } = useAdminLoading();
  const isFirstLoad = useInitialPageLoad();

  const fetchEvent = useCallback(async () => {

    if (isFirstLoad) showLoading();
    setIsLoading(true);

    const params: SearchEventParams = {
      limit: PAGE_SIZE,
      offset: (currentPage - 1) * PAGE_SIZE,
    };

    if (searchValue) params.search = searchValue;

    switch (filterValue) {
      case "draft":
        params.publish_status = 1;
        break;
      case "awaiting_confirmation":
        params.publish_status = 2;
        break;
      case "published":
        params.publish_status = 3;
        break;
    }

    const response = await getEvents({ params });

    const list = Array.isArray(response)
      ? response.map((item) => ({
          ...item,
          full_name: item.user?.full_name,
        }))
      : [];

    setData(list);

    if (list.length < PAGE_SIZE) {
      const calculatedTotal = (currentPage - 1) * PAGE_SIZE + list.length;
      setTotalItems(calculatedTotal);
    } else {
      setTotalItems(currentPage * PAGE_SIZE + 1);
    }

    setIsLoading(false);
    if (isFirstLoad) hideLoading();
  }, [currentPage, searchValue, filterValue]);

  useEffect(() => {
    fetchEvent();
  }, [fetchEvent]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleFilterChange = (value: string) => {
    setFilterValue(value);
    setCurrentPage(1);
  };

  const handleSearchChange = (value: string) => {
    setSearchValue(value);
    setCurrentPage(1);
  };

  const handleOnRowClick = (row:  Event) => {
    window.open(`https://danajou.com/edit-event/${row.id}?isAdminEditing=true`, "_blank")
  };

  const handleRenderActions = (row:  Event) => {
    return (
      <EventAction
        event={row}
        onActionSuccessed={() => fetchEvent()}
      />
    );
  };

  const columns: Column<Event>[] = [
    {
      key: "title",
      header: "عنوان",
      align: "right",
      sortable: true,
      width: "25%",
      accessor: (row) => row.title,
    },
    {
      key: "full_name",
      header: "برگزار کننده",
      align: "right",
      sortable: true,
      width: "25%",
      accessor: (row) => (
        <div className="flex items-center gap-2">
          <img
            className="h-[20px] w-[20px] rounded-full object-cover"
            src={process.env.REACT_APP_File_URL + row.user.avatar || "/images/avatar-alt.jpg"}
            alt=""
            onError={(e) => {
              e.currentTarget.onerror = null;
              e.currentTarget.src = "/images/avatar-alt.jpg";
            }}
          />
          <span>{row.user?.full_name || "بدون نام"}</span>
        </div>
      ),
    },
    {
      key: "starting_at",
      header: "تاریخ شروع",
      align: "right",
      sortable: true,
      width: "15%",
      accessor: (row) => (
        <div className="flex items-center gap-2">
          <span>
            {row.starting_at
              ? new Date(row.starting_at).toLocaleDateString("fa-IR")
              : "بدون تاریخ"}
          </span>
        </div>
      ),
    },
    {
      key: "event_price",
      header: "قیمت",
      align: "right",
      sortable: true,
      width: "10%",
      accessor: (row) => (
        <span className="text-sm text-gray-700">
          {row.event_price > 0
            ? row.event_price
            : "رایگان"}
        </span>
      ),
    },
    {
      key: "",
      header: "عملیات",
      align: "right",
      sortable: false,
      width: "10%",
      accessor: (row) => (
        <EventActionExtra />
      ),
    }
  ];

  return (
    <DataTable
      title="لیست رویدادها"
      data={data}
      columns={columns}
      loading={isLoading}
      loadingText="در حال بارگذاری رویدادها..."
      emptyText="رویدادی یافت نشد"

      searchable={true}
      searchPlaceholder="عنوان"
      searchKeys={["title"]}
      searchValue={searchValue}
      onSearchChange={handleSearchChange}

      filterable={true}
      filterPlaceholder="همه رویدادها"
      filterOptions={filterOptions}
      filterValue={filterValue}
      onFilterChange={handleFilterChange}

      pagination={true}
      serverSidePagination={true}
      currentPage={currentPage}
      onPageChange={handlePageChange}
      totalItems={totalItems}
      pageSize={PAGE_SIZE}

      onRowClick={handleOnRowClick}
      renderActions={handleRenderActions}
    />
  );
};